import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { AskAboutButton } from "@/features/chat/AskAboutButton";

/**
 * A titled content card used to frame charts and tables.
 *
 * **The card always fills its grid cell** (`h-full` + column flex). Panels are
 * laid out in `grid` rows, and a grid item stretches to the row's height by
 * default — but only the *item*. A Panel that was not itself the grid item (the
 * common `<div className="xl:col-span-2"><Panel/></div>` wrapper) kept its
 * natural height inside a stretched wrapper, so the two cards in a row ended at
 * different heights and the row read as ragged. Owning the fill here fixes every
 * row at once instead of each page rediscovering it: `consumption/panels.tsx`
 * had already worked around exactly this locally, which is the duplication this
 * removes.
 *
 * Content is laid out in a column, so a child that should absorb the leftover
 * height opts in with `min-h-0 flex-1` (the `min-h-0` matters — a flex item's
 * default `min-height:auto` refuses to shrink below its content and would push
 * the card taller instead). Children that don't opt in simply sit at the top,
 * exactly as before.
 *
 * `askQuestion` opts a panel into the assistant: the header gains an "ask about
 * this" control carrying that question. It is opt-in per panel rather than
 * automatic, because an affordance on every card is noise, and only the page
 * knows how to phrase a question its figures can actually answer.
 *
 * The import of the assistant's button is a deliberate exception to the usual
 * direction — a shared component reaching into a feature. The alternative is
 * every call site importing and composing the button itself, for an affordance
 * that belongs in the same place on every panel. It stays cheap because
 * `AskAboutButton` and the intent module it uses pull in nothing else from the
 * feature, so the assistant's own code remains lazily loaded; and it stays safe
 * because the button renders nothing when no assistant is mounted.
 *
 * `windowDays` states the trailing window a card is computed over, as a muted
 * "Last N days" beside the title. Pass it on every card whose window is fixed
 * rather than driven by the header's range picker — the home page and the
 * fixed-window panels — so two cards showing different windows on one screen
 * say so instead of looking contradictory. Cards that follow the header scope
 * leave it out: the picker already states their window.
 */
export function Panel({
  title,
  action,
  askQuestion,
  windowDays,
  children,
  className = "",
}: {
  title: string;
  action?: ReactNode;
  askQuestion?: string;
  windowDays?: number;
  children: ReactNode;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <section
      className={`flex h-full flex-col rounded-card border border-border bg-bg-elevated p-5 shadow-sm ${className}`}
    >
      <header className="mb-4 flex shrink-0 items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
          <h2 className="text-sm font-semibold text-fg">{title}</h2>
          {windowDays != null && (
            <span className="text-xs text-fg-subtle">
              {t("controls.lastDays", { count: windowDays })}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {action}
          {askQuestion && <AskAboutButton question={askQuestion} />}
        </div>
      </header>
      {children}
    </section>
  );
}
